import { canonicalizeLegacyServiceLevel, ServiceLevel } from '@provision/service-level-utils';
import { HolidayWindow, TimeWindow } from '@provision/sidekick-utils';
import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DATE_TIME_FORMAT } from 'src/constants/date';
import { usePartnerHolidays, usePartners } from 'src/graphql';
import { usePartnerBusinessHours } from 'src/graphql/entities/partner-business-hours';
import { useSignalBusinessHours } from 'src/graphql/entities/signal-business-hours/hooks/use-signal-business-hours';
import { parsePartnerDateTime } from 'src/utils/partner-datetime';

interface UseEscalationEvaluationResult {
  serviceLevel: string;
  setServiceLevel: (value: string) => void;
  currentTime: Dayjs;
  setCurrentTime: (value: Dayjs) => void;
  timezone: string | undefined;
  /**
   * Partner's actual business hours. Used for response deadline calculations
   * like "by end of day".
   */
  businessHours: TimeWindow[];
  /**
   * Hours during which partners accept escalation notifications.
   * Used for condition matching (e.g., "during business hours" vs "outside business hours").
   * Falls back to businessHours when signal hours are not configured.
   */
  escalationCutoffHours: TimeWindow[];
  holidays: HolidayWindow[];
  isHoliday: boolean;
  setIsHoliday: (value: boolean) => void;
  isPreLits: boolean;
  getEscalationCutoffHoursForUrgency: (urgencyLevel: string) => TimeWindow[];
}

export function useEscalationEvaluation(
  effectivePartnerId: number | undefined,
  contextServiceLevel?: string,
): UseEscalationEvaluationResult {
  const { partners, rawPartnerMap } = usePartners();
  const [serviceLevel, setServiceLevel] = useState<string>(
    canonicalizeLegacyServiceLevel(contextServiceLevel) ?? ServiceLevel.NonMember,
  );
  const [currentTime, setCurrentTime] = useState(dayjs());
  const [isHoliday, setIsHoliday] = useState(false);

  // syync service level when the conversation context changes,
  // but only if the user hasn't manually overridden it via demo controls.
  const hasManualOverride = useRef(false);

  const handleSetServiceLevel = useCallback((value: string) => {
    hasManualOverride.current = true;
    setServiceLevel(canonicalizeLegacyServiceLevel(value) ?? ServiceLevel.NonMember);
  }, []);

  useEffect(() => {
    if (contextServiceLevel && !hasManualOverride.current) {
      setServiceLevel(
        canonicalizeLegacyServiceLevel(contextServiceLevel) ?? ServiceLevel.NonMember,
      );
    }
  }, [contextServiceLevel]);

  const timezone = useMemo(() => {
    if (effectivePartnerId === undefined) return undefined;
    return partners?.[effectivePartnerId]?.timeZone ?? undefined;
  }, [effectivePartnerId, partners]);

  const isPreLits = useMemo(() => {
    if (effectivePartnerId === undefined || !rawPartnerMap) return false;
    const raw =
      rawPartnerMap instanceof Map ? rawPartnerMap.get(effectivePartnerId) : undefined;
    const litsDate = (raw as Record<string, unknown> | undefined)?.litsDate;
    if (!litsDate || typeof litsDate !== 'string') return false;
    return dayjs().isBefore(dayjs(litsDate), 'day');
  }, [effectivePartnerId, rawPartnerMap]);

  const { signalBusinessHours } = useSignalBusinessHours();

  // Partner's actual business hours - used for deadline calculations
  const { partnerBusinessHours } = usePartnerBusinessHours(
    { partnerId: effectivePartnerId },
    { skip: effectivePartnerId === undefined },
  );

  const { partnerHolidays } = usePartnerHolidays(
    { partnerId: effectivePartnerId, filters: [] },
    { skip: effectivePartnerId === undefined },
  );

  // Partner's actual business hours for response deadline calculations
  const businessHours = useMemo<TimeWindow[]>(() => {
    return partnerBusinessHours.map((window) => ({
      weekDay: window.weekDay ?? 0,
      timeFrom: window.timeFrom ?? '',
      timeTo: window.timeTo ?? '',
    }));
  }, [partnerBusinessHours]);

  // index signal business hours by urgency level for quick lookup
  const signalHoursByUrgency = useMemo<Record<string, TimeWindow[]>>(() => {
    const map: Record<string, TimeWindow[]> = {};
    signalBusinessHours.forEach((entry) => {
      const key = entry.urgency;
      if (!map[key]) map[key] = [];
      map[key].push({
        weekDay: typeof entry.weekDay === 'number' ? entry.weekDay : 0,
        timeFrom: entry.timeFrom ?? '',
        timeTo: entry.timeTo ?? '',
      });
    });
    return map;
  }, [signalBusinessHours]);

  // Escalation cutoff hours: prefer signal hours for 'normal' urgency, fall back to partner hours
  const escalationCutoffHours = useMemo<TimeWindow[]>(() => {
    const signalNormal = signalHoursByUrgency.normal;
    if (signalNormal && signalNormal.length > 0) {
      return signalNormal;
    }
    return businessHours;
  }, [signalHoursByUrgency, businessHours]);

  const getEscalationCutoffHoursForUrgency = useCallback(
    (urgencyLevel: string): TimeWindow[] => {
      const key = urgencyLevel.toLowerCase();
      const urgencyHours = signalHoursByUrgency[key];
      if (urgencyHours && urgencyHours.length > 0) {
        return urgencyHours;
      }
      return escalationCutoffHours;
    },
    [signalHoursByUrgency, escalationCutoffHours],
  );

  const holidays = useMemo<HolidayWindow[]>(() => {
    const base = partnerHolidays.map((holiday) => ({
      holidayStart: formatPartnerDateTime(holiday.holidayStart, timezone),
      holidayEnd: formatPartnerDateTime(holiday.holidayEnd, timezone),
    }));

    if (isHoliday) {
      return [
        ...base,
        {
          holidayStart: formatPartnerDateTime(currentTime.startOf('day'), timezone),
          holidayEnd: formatPartnerDateTime(currentTime.endOf('day'), timezone),
        },
      ];
    }

    return base;
  }, [partnerHolidays, isHoliday, currentTime, timezone]);

  return {
    serviceLevel,
    setServiceLevel: handleSetServiceLevel,
    currentTime,
    setCurrentTime,
    timezone,
    businessHours,
    escalationCutoffHours,
    holidays,
    isHoliday,
    setIsHoliday,
    isPreLits,
    getEscalationCutoffHoursForUrgency,
  };
}

function formatPartnerDateTime(value: Dayjs, timezone?: string): string {
  return timezone
    ? parsePartnerDateTime(value.format(DATE_TIME_FORMAT), timezone).toISOString()
    : value.toISOString();
}
